/* eslint-disable @typescript-eslint/no-unused-vars */
import { yupResolver } from '@hookform/resolvers/yup';
import Stack from '@mui/material/Stack';
import { isEmpty } from 'lodash-es';
import React from 'react';
import { useForm, FieldValues, FormProvider } from 'react-hook-form';
import { object, string, number } from 'yup';

import SignupFormDefaultValues from '../../formDefaultValues/SignupFormDefaultValues';
import ISingupParams from '../../types/ISignupParams';
import { ControlledTextInput } from '../ControlledTextInput';
import { Item } from '../Item';
import { WsButton } from '../WsButton';

const validationSchema = object().shape({
  email: string().required('Email is required').email('Email is invalid'),
  password: string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
  name: string().required('Full name is required'),
  phoneNumber: number()
    .typeError("That doesn't look like a phone number")
    .positive("A phone number can't start with a minus")
    .integer("A phone number can't include a decimal point")
    .required('Phone number is required'),
});

interface ISignupForm {
  emailParam: string | null;
  amplitudeHandler?: () => any;
  validateEmail(args: string): void;
  onSubmit(args: ISingupParams): void;
  resetForm?: boolean;
  errorProp?: {
    field: string;
    message: string;
    isError: boolean;
  };
}

const SignupForm: React.FC<ISignupForm> = ({
  emailParam,
  amplitudeHandler,
  validateEmail,
  onSubmit,
  resetForm,
  errorProp,
}) => {
  const formOptions = {
    resolver: yupResolver(validationSchema),
    defaultValues: SignupFormDefaultValues,
  };

  const methods = useForm(formOptions);

  const { handleSubmit, reset, setError, clearErrors, setValue } = methods;

  emailParam && setValue('email', emailParam);

  React.useEffect(() => {
    if (resetForm) {
      reset();
    }
    if (errorProp) {
      if (errorProp.isError) {
        setError(errorProp.field, {
          type: 'manual',
          message: errorProp.message,
        });
      } else {
        clearErrors();
      }
    }
  }, [resetForm, errorProp, reset, setError, clearErrors]);

  const onFormSubmit = (data: FieldValues) => {
    const formData: ISingupParams = {
      email: data.email,
      name: data.name,
      phoneNumber: data.phoneNumber,
      password: data.password,
    };
    if (amplitudeHandler) amplitudeHandler();
    onSubmit(formData);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Item>
          <Stack spacing={1} direction="column">
            <ControlledTextInput
              name={'email'}
              labelText="Email address *"
              onBlur={(e) => validateEmail(e.target.value)}
              type="email"
              fullWidth
              id="email"
              placeholder="Eg, johndoe@email.com"
              variant="outlined"
              required
              disabled={!isEmpty(emailParam)}
              autoComplete="email"
            />
          </Stack>
          <Stack mt={1} spacing={1} direction="column">
            <ControlledTextInput
              name={'name'}
              labelText="Full Name *"
              fullWidth
              id="full-name"
              placeholder="Eg, John Do"
              variant="outlined"
              required
              autoComplete="name"
            />
          </Stack>
          <Stack mt={1} spacing={1} direction="column">
            <ControlledTextInput
              name={'phoneNumber'}
              labelText="Phone Number *"
              fullWidth
              id="phone-number"
              placeholder="Eg, 99999 - 99999"
              variant="outlined"
              required
              autoComplete="phoneNumber"
            />
          </Stack>
          <Stack mt={1} spacing={1} direction="column">
            <ControlledTextInput
              name={'password'}
              labelText="Enter Password *"
              type="password"
              fullWidth
              id="password"
              placeholder="Atleast 8 characters"
              variant="outlined"
              required
              autoComplete="password"
            />
          </Stack>
          <Stack mt={2}>
            <WsButton variant="contained" size="large" fullWidth type="submit">
              Sign up
            </WsButton>
          </Stack>
        </Item>
      </form>
    </FormProvider>
  );
};
export default SignupForm;
